import { useCallback, useEffect, useMemo } from 'react'

import { ChevronDownIcon, FilterIcon, SlidersHorizontalIcon } from 'lucide-react'
import {
  Badge,
  ComboboxMulti,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  FormGroup,
  Label,
  Select,
} from 'twyne-new.ui'

import { useCustomReportStore } from '../store'
import {
  CUSTOM_FILTER_ENTITY,
  DEFAULT_CUSTOM_FILTER_OPERATOR,
  getCollectionFields,
  getCustomFilterSummary,
  getFieldOperators,
  isCustomFilterComplete,
  isOperatorAllowed,
  isValuelessOperator,
} from '../utils/customFilters'
import {
  ENTITY_LIST_CONFIG,
  buildEntityListParams,
  getAvailableFilterEntities,
  getEntityLabel,
  mapEntityItems,
} from '../utils/filterEntities'

import ConfigField, { CONFIG_CONTROL_CLASS, ConfigFieldRemoveButton } from './ConfigField'
import CustomFilterValueInput from './CustomFilterValueInput'

const TRIGGER_CLASS = `${CONFIG_CONTROL_CLASS} flex min-h-9 items-center justify-between gap-1 p-1 cursor-pointer rounded-xs border twy-border-base twy-bg-base-accent`

function EmptyState({ children }) {
  return <p className="px-3 py-2 text-sm twy-text-base-muted">{children}</p>
}

function CustomFilterForm({ collection, filter, onChange }) {
  const fieldOptions = useMemo(() => getCollectionFields(collection), [collection])

  const operatorOptions = useMemo(
    () => getFieldOperators(collection, filter.field),
    [collection, filter.field],
  )

  const handleValueChange = useCallback(value => {
    onChange({ ...filter, value })
  }, [filter, onChange])

  const handleFieldChange = useCallback(option => {
    const field = option?.value || null
    const keepsOperator = isOperatorAllowed(collection, field, filter.operator)

    onChange({
      ...filter,
      field,
      operator: keepsOperator ? filter.operator : DEFAULT_CUSTOM_FILTER_OPERATOR,
      value: keepsOperator ? filter.value : '',
    })
  }, [collection, filter, onChange])

  if (!fieldOptions.length) {
    return <EmptyState>No fields available for this collection.</EmptyState>
  }

  return <div className="flex flex-col gap-4 p-4">
    <FormGroup>
      <Label>Field</Label>
      <Select
        options={fieldOptions}
        value={filter.field ?? null}
        onChange={handleFieldChange}
        placeholder="Select the field"
      />
    </FormGroup>
    <FormGroup>
      <Label>Condition</Label>
      <Select
        options={operatorOptions}
        value={filter.operator ?? null}
        onChange={option => onChange({ ...filter, operator: option?.value || null })}
        placeholder="Select the condition"
        disabled={!filter.field}
      />
    </FormGroup>
    {!isValuelessOperator(filter.operator) && <FormGroup>
      <Label>Value</Label>
      <CustomFilterValueInput
        collection={collection}
        filter={filter}
        onChange={handleValueChange}
      />
    </FormGroup>}
  </div>
}

export default function FilterByDropdown({
  filter,
  collection,
  activeEntities = [],
  onChange,
  onRemove,
}) {
  const { lists, siteSelected } = useCustomReportStore()
  const { entity, values = [] } = filter
  const isCustom = entity === CUSTOM_FILTER_ENTITY
  const config = entity ? ENTITY_LIST_CONFIG[entity] : null
  const listStore = config ? lists?.[config.listKey] : null

  const loadList = listStore?.list
  const items = listStore?.items

  const listParams = useMemo(
    () => buildEntityListParams(siteSelected?.id),
    [siteSelected?.id],
  )

  useEffect(() => {
    if (entity && loadList) loadList(listParams)
  }, [entity, loadList, listParams])

  const options = useMemo(
    () => config ? mapEntityItems(items, config).map(item => ({
      value: String(item.value),
      label: item.label,
    })) : [],
    [config, items],
  )

  const selectedValues = useMemo(() => values.map(String), [values])

  const customSummary = useMemo(() => {
    if (!isCustom || !isCustomFilterComplete(filter)) return null
    return getCustomFilterSummary(collection, filter)
  }, [isCustom, filter, collection])

  const entityOptions = useMemo(
    () => getAvailableFilterEntities(collection, activeEntities.filter(item => item !== entity))
      .map(item => ({ value: item, label: getEntityLabel(item) })),
    [collection, activeEntities, entity],
  )

  const handleSelectEntity = useCallback(option => {
    if (!option?.value) return

    onChange({
      ...filter,
      entity: option.value,
      values: [],
      field: null,
      operator: null,
      value: '',
    })
  }, [filter, onChange])

  const handleValuesChange = useCallback(nextValues => {
    onChange({ ...filter, values: nextValues })
  }, [filter, onChange])

  const removeAction = <ConfigFieldRemoveButton label="Remove filter" onClick={onRemove} />

  if (isCustom) {
    return (
      <ConfigField label="Custom filter" icon={SlidersHorizontalIcon} action={removeAction}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className={TRIGGER_CLASS}>
              {customSummary ? <Badge color="accent" variant="light" className="m-0.5 truncate font-normal">
                {customSummary}
              </Badge> : <span className="mx-2 text-gray-600 dark:text-gray-1000">Set the condition</span>}
              <ChevronDownIcon size={12} className="mr-1 shrink-0 text-gray-600" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-72 p-0 twy-bg-base-accent">
            <CustomFilterForm
              collection={collection}
              filter={filter}
              onChange={onChange}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      </ConfigField>
    )
  }

  if (!entity) {
    return (
      <ConfigField label="Filter by" icon={FilterIcon} action={removeAction}>
        <Select
          className={CONFIG_CONTROL_CLASS}
          controlClassName="rounded-l-none rounded-r-none"
          options={entityOptions}
          value={null}
          onChange={handleSelectEntity}
          placeholder="Select the filter"
          disabled={!entityOptions.length}
        />
      </ConfigField>
    )
  }

  return (
    <ConfigField label={getEntityLabel(entity)} icon={FilterIcon} action={removeAction}>
      <ComboboxMulti
        className={CONFIG_CONTROL_CLASS}
        options={options}
        value={selectedValues}
        onChange={handleValuesChange}
        placeholder={`Select ${getEntityLabel(entity)}`}
        hideSelectAll
        maxCount={1}
        disabled={!options.length}
      />
    </ConfigField>
  )
}
